// Uses granular columnOption() calls rather than DevExtreme's state() API.
// state() triggers a full grid reload (data re-fetch + re-render). For admin
// database-backed datagrids this would be acceptable (the server re-queries with
// the new filters), but older-gen datagrids load all data into memory upfront --
// a full reload just to apply a saved filter is unreasonable.
// columnOption() updates each property individually without reloading data.

// Column properties worth persisting in a view config.
const COLUMN_KEYS = [
  "dataField",
  "name",
  "filterType",
  "filterValue",
  "filterValues",
  "groupIndex",
  "selectedFilterOperation",
  "sortOrder",
  "visibleIndex",
  "width",
];

/**
 * Reads the current column configuration from the datagrid, compacted for storage.
 * @param {Object} datagrid - The DevExtreme datagrid instance
 * @returns {Array} Array of compacted column config objects
 */
export function getViewColumns(datagrid) {
  return getVisibleDataColumns(datagrid).map(compactColumn);
}

/**
 * Visible columns bound to a data field. Command columns (row selection checkboxes, group expanders)
 * have no dataField: they cannot be restored by applyViewConfig and have no caption to display.
 * @param {Object} datagrid - The DevExtreme datagrid instance
 * @returns {Array} Visible column objects
 */
export function getVisibleDataColumns(datagrid) {
  return datagrid.getVisibleColumns().filter((column) => column.dataField);
}

/**
 * Applies a saved view configuration to the datagrid.
 * @param {Object} datagrid - The DevExtreme datagrid instance
 * @param {Array} columnConfigs - Array of column configuration objects
 */
export function applyViewConfig(datagrid, columnConfigs) {
  const visibleDataFields = new Set(columnConfigs.map((c) => c.dataField));
  const { allColumns, bandParentsToShow } = resolveVisibleFields(datagrid, visibleDataFields);

  datagrid.beginUpdate();
  try {
    // Hide all columns first
    for (const col of allColumns) {
      datagrid.columnOption(col.dataField, "visible", false);
      datagrid.columnOption(col.dataField, "filterType", undefined);
      datagrid.columnOption(col.dataField, "filterValue", undefined);
      datagrid.columnOption(col.dataField, "filterValues", undefined);
      datagrid.columnOption(col.dataField, "selectedFilterOperation", undefined);
      datagrid.columnOption(col.dataField, "groupIndex", undefined);
      datagrid.columnOption(col.dataField, "sortOrder", undefined);
    }

    // Band columns (parents with nested children) aren't stored in the view config,
    // but DevExtreme hides children when their parent is hidden.
    for (const columnName of bandParentsToShow) {
      datagrid.columnOption(columnName, "visible", true);
    }

    // Apply saved column settings
    for (const column of columnConfigs) {
      const columnDataType = datagrid.columnOption(column.dataField, "dataType");
      const filterValue = deserializeFilterValue(column.filterValue, columnDataType);

      datagrid.columnOption(column.dataField, "visible", true);
      datagrid.columnOption(column.dataField, "filterType", column.filterType);
      datagrid.columnOption(column.dataField, "filterValues", column.filterValues);
      datagrid.columnOption(column.dataField, "filterValue", filterValue);
      datagrid.columnOption(column.dataField, "selectedFilterOperation", column.selectedFilterOperation);
      datagrid.columnOption(column.dataField, "width", column.width);
      datagrid.columnOption(column.dataField, "groupIndex", column.groupIndex);
      datagrid.columnOption(column.dataField, "sortOrder", column.sortOrder);
      datagrid.columnOption(column.dataField, "visibleIndex", column.visibleIndex);
    }
  } finally {
    datagrid.endUpdate();
  }
}

// --- Internal helpers ---

function compactColumn(col) {
  const out = {};
  for (const key of COLUMN_KEYS) {
    // name is redundant when it matches dataField
    if (key === "name" && col[key] === col.dataField) continue;
    if (col[key] !== undefined) {
      out[key] = key === "width" && typeof col[key] === "number" ? Math.round(col[key]) : col[key];
    }
  }
  if (col.columns) {
    out.columns = col.columns.filter((c) => c.visible !== false).map(compactColumn);
  }
  return out;
}

/**
 * Deserializes a filter value, converting ISO date strings back to Date objects.
 * This is necessary because Date objects are serialized to strings when saved as JSON.
 * @param {*} filterValue - The filter value to deserialize
 * @param {string} dataType - The column's data type (e.g., "date", "string", "number")
 * @returns {*} The deserialized filter value with Date objects restored for date columns
 */
function deserializeFilterValue(filterValue, dataType) {
  if (dataType !== "date" || filterValue == null) {
    return filterValue;
  }

  if (Array.isArray(filterValue)) {
    return filterValue.map((value) => (typeof value === "string" ? new Date(value) : value));
  }

  return typeof filterValue === "string" ? new Date(filterValue) : filterValue;
}

/**
 * Given a nested column tree and a set of dataFields that should be visible,
 * returns a flat list of all columns and band parents that must stay shown.
 *
 * Band parents aren't stored in the view config, but DevExtreme hides children
 * when their parent is hidden, so ancestors of visible columns must stay shown.
 *
 * @param {Object} datagrid - The DevExtreme datagrid instance
 * @param {Set<string>} visibleDataFields - The dataFields saved in the view config
 * @returns {{ allColumns: Array, bandParentsToShow: Set }}
 */
function resolveVisibleFields(datagrid, visibleDataFields) {
  const allColumns = [];
  const bandParentsToShow = new Set();

  (function explore(columns, ancestors) {
    for (const col of columns) {
      allColumns.push(col);

      if (visibleDataFields.has(col.dataField)) {
        for (const ancestor of ancestors) {
          bandParentsToShow.add(ancestor);
        }
      }

      if (col.columns && col.columns.length > 0) {
        explore(col.columns, [...ancestors, col.dataField]);
      }
    }
  })(datagrid.option("columns"), []);

  return { allColumns, bandParentsToShow };
}
